import {
    CreateUser,
    isOfTypeUser,
    Permission,
    permissionTranslation,
    User,
} from '@/models/user';
import React, { FunctionComponent, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { set } from 'lodash';
import { DrawerActions } from '@/components/Drawer/DrawerActions';
import { LoadingButton } from '@mui/lab';
import { useUpsertUser } from '@/pages/userManagement/hooks/useUpsertUser';
import { useUpdateLiftOperatorUser } from '@/pages/userManagement/hooks/useUpdateLiftOperator';

type UserDrawerContentProps = {
    user: User | CreateUser;
    onClose: () => void;
    isLiftOperatorEdit?: boolean;
};

export const UserDrawerContent: FunctionComponent<UserDrawerContentProps> = ({
    user,
    onClose,
    isLiftOperatorEdit = false,
}) => {
    const [localUser, setLocalUser] = useState<User | CreateUser>(user);
    const [passwordRepeat, setPasswordRepeat] = useState<string>('');

    const { upsertUser, isLoading: loadingUpsertUser } = useUpsertUser({
        onSuccess: (savedUser) => {
            setLocalUser(savedUser);
            onClose();
        },
    });
    const { updateLiftOperatorPassword, isLoading: loadingLiftoperatorUser } =
        useUpdateLiftOperatorUser();

    useEffect(() => {
        setLocalUser(user);
    }, [user]);

    const onChangeHandler = (key: string) => (e) => {
        const { value } = e.target;

        setLocalUser((prevLocalWebshop) => {
            const copy = { ...prevLocalWebshop };

            set(copy, key, value);
            return copy;
        });
    };

    const onCheckboxChange = (permission: Permission) => (e) => {
        const { checked } = e.target;

        const copyPermissions = [...localUser.permissions];
        if (checked) {
            copyPermissions.push(permission);
        } else {
            const pIndex = copyPermissions.indexOf(permission);

            if (pIndex > -1) {
                copyPermissions.splice(pIndex, 1);
            }
        }

        setLocalUser((prevUser) => ({
            ...prevUser,
            permissions: [...copyPermissions],
        }));
    };

    const onCreateOrUpdate = () => {
        if (isLiftOperatorEdit) {
            updateLiftOperatorPassword(localUser.password);
        } else {
            upsertUser(localUser);
        }
    };

    const isSaveDisabled = () => {
        if (isLiftOperatorEdit) {
            return (
                !localUser.password ||
                !passwordRepeat ||
                passwordRepeat !== localUser.password
            );
        }

        if (isOfTypeUser(localUser)) {
            return (
                !localUser.username ||
                (localUser.password && passwordRepeat !== localUser.password)
            );
        }

        return !(
            localUser.username &&
            localUser.password &&
            passwordRepeat &&
            passwordRepeat === localUser.password
        );
    };

    return (
        <Box
            sx={{
                width: '480px',
            }}
        >
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.dark,
                    color: '#fff',
                    p: 4,
                })}
            >
                <Typography variant='h6'>
                    {isLiftOperatorEdit
                        ? 'Bergbahn Passwort ändern'
                        : isOfTypeUser(localUser)
                          ? 'Benutzer bearbeiten'
                          : 'Neuen Benutzer erstellen'}
                </Typography>
            </Box>

            <Stack
                spacing={3}
                sx={{
                    p: 4,
                }}
            >
                <Grid2 container spacing={3}>
                    <Grid2 size={12}>
                        <TextField
                            value={localUser.username || ''}
                            onChange={onChangeHandler('username')}
                            label='Username'
                            disabled={isLiftOperatorEdit}
                            sx={{
                                width: '100%',
                            }}
                        />
                    </Grid2>

                    <Grid2 size={12}>
                        <TextField
                            onChange={onChangeHandler('password')}
                            type='password'
                            label='Passwort'
                            sx={{
                                width: '100%',
                            }}
                        />
                    </Grid2>

                    <Grid2 size={12}>
                        <TextField
                            value={passwordRepeat}
                            onChange={(e) => setPasswordRepeat(e.target.value)}
                            type='password'
                            label='Passwort wiederholen'
                            error={
                                !!passwordRepeat &&
                                passwordRepeat !== localUser.password
                            }
                            helperText={
                                !!passwordRepeat &&
                                passwordRepeat !== localUser.password
                                    ? 'Passwörter stimmen nicht überein'
                                    : ''
                            }
                            sx={{
                                width: '100%',
                            }}
                        />
                    </Grid2>

                    {!isLiftOperatorEdit && (
                        <Grid2 size={12}>
                            <TextField
                                value={localUser.company || ''}
                                onChange={onChangeHandler('company')}
                                label='Firma'
                                sx={{
                                    width: '100%',
                                }}
                            />
                        </Grid2>
                    )}

                    {!isLiftOperatorEdit && (
                        <Grid2 size={12}>
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: '1.2rem',
                                    mb: 3,
                                }}
                            >
                                Seiten für Benutzer freigeben:
                            </Typography>
                            <UserPermissionsGrid
                                userPermissions={localUser.permissions}
                                onPermissionChange={onCheckboxChange}
                            />
                        </Grid2>
                    )}
                </Grid2>

                <DrawerActions>
                    <Stack
                        sx={{
                            alignItems: 'center',
                        }}
                        spacing={2}
                        direction='row'
                    >
                        <Button variant='outlined' onClick={onClose}>
                            Abbrechen
                        </Button>

                        <LoadingButton
                            variant='contained'
                            onClick={onCreateOrUpdate}
                            loading={
                                loadingUpsertUser || loadingLiftoperatorUser
                            }
                            disabled={isSaveDisabled()}
                        >
                            Speichern
                        </LoadingButton>
                    </Stack>
                </DrawerActions>
            </Stack>
        </Box>
    );
};

type UserPermissionsGridProps = {
    userPermissions: Permission[];
    onPermissionChange: (
        permission: Permission
    ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const UserPermissionsGrid: FunctionComponent<UserPermissionsGridProps> = ({
    userPermissions,
    onPermissionChange,
}) => {
    return (
        <Grid2 container spacing={3}>
            {Object.keys(permissionTranslation).map(
                (permissionKey: Permission) => {
                    // this is just needed for
                    if (permissionKey === Permission.ADMIN) return null;

                    const checked = userPermissions.includes(permissionKey);

                    return (
                        <Grid2 key={permissionKey} size={{ xs: 12, md: 6 }}>
                            <FormControlLabel
                                label={permissionTranslation[permissionKey]}
                                control={
                                    <Checkbox
                                        checked={checked}
                                        onChange={onPermissionChange(
                                            permissionKey
                                        )}
                                    />
                                }
                            />
                        </Grid2>
                    );
                }
            )}
        </Grid2>
    );
};
